import React from "react";
import { LOGOS } from "../config";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { oneLight } from "react-syntax-highlighter/dist/esm/styles/prism";
import "./ChatMessage.css";
import Avatar from "./Avatar";

function VideoCard({ video }) {
  const [open, setOpen] = React.useState(false);
  const [maximized, setMaximized] = React.useState(false);
  const videoRef = React.useRef(null);

  const handleNativeFullscreen = () => {
    const el = videoRef.current;
    if (!el) return;
    if (el.requestFullscreen) el.requestFullscreen();
    else if (el.webkitRequestFullscreen) el.webkitRequestFullscreen();
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) setOpen(false);
  };

  // Close modal on Escape key
  React.useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  return (
    <>
      <div
        className="video-card"
        onClick={() => setOpen(true)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => e.key === "Enter" && setOpen(true)}
      >
        <div className="video-thumb-wrap">
          <img
            src={video.thumbnail}
            alt={video.title}
            className="video-thumb"
          />
          <div className="video-play-overlay">
            <svg width="48" height="48" viewBox="0 0 48 48">
              <circle cx="24" cy="24" r="24" fill="rgba(0,0,0,0.55)" />
              <polygon points="19,14 36,24 19,34" fill="white" />
            </svg>
          </div>
          {video.duration && (
            <span className="video-duration">{video.duration}</span>
          )}
        </div>
        <div className="video-info">
          <p className="video-title">{video.title.replace(/^\|\s*/, "")}</p>
          {video.description && (
            <p className="video-desc">{video.description}</p>
          )}
        </div>
      </div>

      {open && (
        <div
          className={`video-modal-overlay${maximized ? " maximized" : ""}`}
          onClick={handleOverlayClick}
        >
          <div className="video-modal">
            <div className="video-modal-header">
              <span className="video-modal-title">
                {video.title.replace(/^\|\s*/, "")}
              </span>
              <div className="video-modal-actions">
                <button
                  className="video-modal-btn"
                  onClick={handleNativeFullscreen}
                  title="Full screen"
                >
                  <svg
                    width="15"
                    height="15"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <polyline points="15 3 21 3 21 9" />
                    <polyline points="9 21 3 21 3 15" />
                    <line x1="21" y1="3" x2="14" y2="10" />
                    <line x1="3" y1="21" x2="10" y2="14" />
                  </svg>
                </button>
                <button
                  className="video-modal-btn"
                  onClick={() => setMaximized((m) => !m)}
                  title={maximized ? "Restore" : "Maximize"}
                >
                  {maximized ? (
                    <svg
                      width="15"
                      height="15"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <polyline points="4 14 10 14 10 20" />
                      <polyline points="20 10 14 10 14 4" />
                      <line x1="10" y1="14" x2="3" y2="21" />
                      <line x1="21" y1="3" x2="14" y2="10" />
                    </svg>
                  ) : (
                    <svg
                      width="15"
                      height="15"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <polyline points="15 3 21 3 21 9" />
                      <polyline points="9 21 3 21 3 15" />
                      <line x1="21" y1="3" x2="14" y2="10" />
                      <line x1="3" y1="21" x2="10" y2="14" />
                    </svg>
                  )}
                </button>
                <button
                  className="video-modal-btn"
                  onClick={() => setOpen(false)}
                  title="Close"
                >
                  <svg
                    width="15"
                    height="15"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </button>
              </div>
            </div>
            <video
              ref={videoRef}
              className="video-player"
              controls
              playsInline
              controlsList="nodownload"
              poster={video.thumbnail}
            >
              <source src={video.link} type="video/mp4" />
            </video>
          </div>
        </div>
      )}
    </>
  );
}

function CopyButton({ text }) {
  const [copied, setCopied] = React.useState(false);
  const handle = () => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };
  return (
    <button className="copy-btn" onClick={handle}>
      {copied ? (
        <svg
          width="13"
          height="13"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
        >
          <polyline points="20 6 9 17 4 12" />
        </svg>
      ) : (
        <svg
          width="13"
          height="13"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <rect x="9" y="9" width="13" height="13" rx="2" />
          <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
        </svg>
      )}
      {copied ? "Copied" : "Copy"}
    </button>
  );
}

const mdComponents = {
  code({ inline, className, children, ...props }) {
    const match = /language-(\w+)/.exec(className || "");
    const codeText = String(children).replace(/\n$/, "");
    if (!inline && match) {
      return (
        <div className="code-block">
          <div className="code-block-header">
            <span className="code-lang">{match[1]}</span>
            <CopyButton text={codeText} />
          </div>
          <SyntaxHighlighter
            style={oneLight}
            language={match[1]}
            PreTag="div"
            customStyle={{
              margin: 0,
              borderRadius: "0 0 8px 8px",
              fontSize: "13px",
            }}
            {...props}
          >
            {codeText}
          </SyntaxHighlighter>
        </div>
      );
    }
    return (
      <code className="inline-code" {...props}>
        {children}
      </code>
    );
  },
  p: ({ children }) => <p className="md-p">{children}</p>,
  ul: ({ children }) => <ul className="md-ul">{children}</ul>,
  ol: ({ children }) => <ol className="md-ol">{children}</ol>,
  li: ({ children }) => <li className="md-li">{children}</li>,
  blockquote: ({ children }) => (
    <blockquote className="md-blockquote">{children}</blockquote>
  ),
  h1: ({ children }) => <h1 className="md-h1">{children}</h1>,
  h2: ({ children }) => <h2 className="md-h2">{children}</h2>,
  h3: ({ children }) => <h3 className="md-h3">{children}</h3>,
  table: ({ children }) => (
    <div className="md-table-wrap">
      <table className="md-table">{children}</table>
    </div>
  ),
  th: ({ children }) => <th className="md-th">{children}</th>,
  td: ({ children }) => <td className="md-td">{children}</td>,
  a: ({ href, children }) => (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="md-link"
    >
      {children}
    </a>
  ),
  strong: ({ children }) => <strong className="md-strong">{children}</strong>,
  hr: () => <hr className="md-hr" />,
};

export default function ChatMessage({ message, chat_widget_icon, isLast, onFollowUp, onOpenSourceFile }) {
  const isUser = message.role === "user";

  return (
    <div className={`message-row ${isUser ? "user-row" : "assistant-row"}`}>
      {isUser ? (
        <div className="user-bubble">
          <p className="user-text">{message.content}</p>
        </div>
      ) : (
        <>
          <div className="chat-avatar">
            <Avatar
              src={chat_widget_icon || LOGOS.logo}
              alt="Arya"
              className="chat-avatar-img"
            />
          </div>
          <div className="assistant-content">
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={mdComponents}
            >
              {message.content
                .replace(/\\n/g, "\n")
                .replace(/\n(\d+\.)/g, "\n\n$1")}
            </ReactMarkdown>
            {message.videoResult && message.videoResult.length > 0 && (
              <div className="video-results">
                {message.videoResult.map((video) => (
                  <VideoCard key={video.sessionId} video={video} />
                ))}
              </div>
            )}
            {message.imageResult && message.imageResult.length > 0 && (
              <div className="chat-image-results">
                {message.imageResult.map((img) => (
                  <a
                    key={img.resource_id}
                    href={img.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="chat-image-link"
                    title={img.file_name}
                  >
                    <img
                      src={img.link}
                      alt={img.file_name}
                      className="chat-image"
                    />
                    <span className="chat-image-name">{img.file_name}</span>
                  </a>
                ))}
              </div>
            )}
            {message.sourceFileData && message.sourceFileData.length > 0 && (
              <div className="source-files">
                <span className="source-files-label">Sources</span>
                <div className="source-file-chips">
                  {message.sourceFileData.map((file, idx) => (
                    <button
                      key={idx}
                      className="source-file-chip"
                      onClick={() => onOpenSourceFile && onOpenSourceFile(message.sourceFileData, idx)}
                      title={file.name}
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                           stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                        <polyline points="14 2 14 8 20 8"/>
                      </svg>
                      <span className="source-file-chip-name">{file.name || 'Untitled'}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
            {isLast && message.followUpQuestions && message.followUpQuestions.length > 0 && (
              <div className="follow-up-questions">
                {message.followUpQuestions.map((q) => (
                  <button
                    key={q}
                    className="follow-up-chip"
                    onClick={() => onFollowUp(q)}
                  >
                    {q}
                  </button>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
